/**
 * Переходы режима «Предложения».
 *
 * Как и режим «Мои ошибки», режим собирается из двух функций:
 * `feature-sentence-practice` знает, что показать, а
 * `feature-adaptive-practice` — как приоритет материала и итоги ложатся в
 * mastery. Связывает их слой приложения (docs/ARCHITECTURE.md §5).
 */

import type { SessionId } from '@type2x/shared-contracts';
import type { TypingSession } from '@type2x/typing-engine';
import { DEFAULT_LEARNING_CONFIG } from '@type2x/learning-engine';
import type { Workspace } from '@type2x/feature-personal-workspace';
import {
  activeProfile,
  appendXp,
  recordSession,
  upsertMastery,
} from '@type2x/feature-personal-workspace';
import { applyOutcomes, priorityContext } from '@type2x/feature-adaptive-practice';
import {
  advanceSentences,
  collectSentenceOutcomes,
  currentSentence,
  observeSentence,
  selectSentenceDrills,
  sentenceTask,
  startSentencesSession,
  type SentenceSnapshot,
  type SentenceTask,
  type SentencesSession,
} from '@type2x/feature-sentence-practice';
import { buildSessionSummary } from '@type2x/feature-progress';
import { buildSessionPayload } from '@type2x/feature-sync';

import type { Runtime } from '../runtime.js';
import type { AppScreen } from './app-state.js';
import { countXpToday, resultScreen, type Transition } from './flow.js';

const CONFIG = DEFAULT_LEARNING_CONFIG;

/** Снимок навыка для подбора предложений. */
export function sentenceSnapshot(workspace: Workspace): SentenceSnapshot {
  const profile = activeProfile(workspace);

  return {
    language: profile.language,
    layoutId: profile.keyboard.layoutId,
    units: profile.units,
    stats: profile.stats,
  };
}

/**
 * Экран режима либо null, если предложений не нашлось.
 *
 * Пустым набор бывает только при пустом словаре языка: материал переноса
 * доступен и тому, у кого слабых мест не осталось.
 */
export function sentencesScreen(runtime: Runtime, workspace: Workspace): AppScreen | null {
  const snapshot = sentenceSnapshot(workspace);
  const profile = activeProfile(workspace);

  const sessionId = runtime.ids.newSessionId();
  const drills = selectSentenceDrills(
    snapshot,
    CONFIG,
    priorityContext(
      {
        language: profile.language,
        layoutId: profile.keyboard.layoutId,
        units: profile.units,
        stats: profile.stats,
        seenInControl: profile.seenInControl,
        leftHandShare: 0.5,
      },
      runtime.clock.epochMs(),
    ),
    undefined,
    // Зерно перебора — идентификатор занятия: в одном занятии набор
    // постоянен, в следующем приходят другие фразы.
    sessionId,
  );
  if (drills.length === 0) return null;

  return {
    name: 'sentences',
    session: startSentencesSession(snapshot, drills),
    sessionId,
  };
}

/** Засчитывает набранное предложение и переходит к следующему. */
export function advanceSentence(session: SentencesSession, run: TypingSession): SentencesSession {
  const drill = currentSentence(session);
  if (drill === null) return session;

  return advanceSentences(session, {
    observation: observeSentence(drill, run.keystrokeEvents, run.typingErrors),
    metrics: run.metrics,
    errors: run.typingErrors,
  });
}

/** Задание текущего предложения либо null, если сессия закончена. */
export function currentSentenceTask(session: SentencesSession): SentenceTask | null {
  return sentenceTask(session);
}

/**
 * Завершает сессию: обновляет mastery, начисляет XP и собирает итог.
 *
 * Итог собирается так же, как после тренировки: пользователю важен один
 * понятный экран результата, а не отдельный для каждого режима.
 */
export function finishSentences(
  runtime: Runtime,
  workspace: Workspace,
  session: SentencesSession,
  sessionId: SessionId,
): Transition {
  const now = runtime.clock.isoNow();
  const profile = activeProfile(workspace);

  const outcomes = collectSentenceOutcomes(session);
  const statsAfter = applyOutcomes(profile.units, profile.stats, outcomes, CONFIG, now);

  let next = upsertMastery(workspace, [], statsAfter, now);
  next = recordSession(next, session.metrics, now);

  const summary = buildSessionSummary({
    metrics: session.metrics,
    baseline: profile.baseline,
    units: profile.units,
    statsBefore: profile.stats,
    statsAfter,
    previousXp: next.xpLedger.reduce((sum, item) => sum + item.amount, 0),
    xpOccurrencesToday: countXpToday(next, now),
    activeDays: activeProfile(next).practiceDays.length,
    retained: false,
    isControlled: false,
    awardedAt: now,
    config: CONFIG,
  });

  for (const transaction of summary.xpAwarded) {
    next = appendXp(next, transaction, now);
  }

  return {
    workspace: next,
    screen: resultScreen(next, summary, { kind: 'sentences' }, now),
    sessionPayload: buildSessionPayload({
      syncId: runtime.ids.newSyncId(),
      sessionId,
      mode: 'sentences',
      language: profile.language,
      layoutId: profile.keyboard.layoutId,
      startedAt: new Date(runtime.clock.epochMs() - session.metrics.elapsedMs).toISOString(),
      durationMs: session.metrics.elapsedMs,
      metrics: session.metrics,
      outcomes,
      statsAfter,
      errors: session.errors,
      isControlled: false,
    }),
  };
}
